import React, { useMemo } from 'react';

import {
  Button,
  Col,
  List,
  Popover,
  Row,
  Space,
  Typography,
  Tooltip,
} from 'antd';

import {
  CloseOutlined,
  QuestionCircleTwoTone,
  ImportOutlined,
} from '@ant-design/icons';

import { FullWidthSpace } from 'common/components';

import { RequiredActiveProductRoot } from 'common/components/product-add/make-product-active/RequiredActiveProduct';
import {
  ProductsActiveValidating,
  ProductsActiveValidatingModal,
} from './ProductsActiveValidating';

import { useGetRequirementsForActiveProduct } from 'hooks/entityHooks';
import { useRequiredActiveProductModal } from 'common/components/product-add/make-product-active/hooks';

import { additionalRequirementFieldsLowercase } from 'common/components/product-add/make-product-active/constants';

const { Title } = Typography;

export const MissingRequirementProductMessage = ({
  errors,
  entityId,
  onRevalidateActiveProduct,
  primaryErrors,
  productData,
  mode,
  additionalRequirements,
}) => {
  const { allActiveRequirements } = useGetRequirementsForActiveProduct();

  const additionalFields = Object.keys(additionalRequirementFieldsLowercase);

  const {
    visibleModalRequiredActiveProduct,
    onModalRequiredActiveProduct,
    offModalRequiredActiveProduct,
  } = useRequiredActiveProductModal();

  const requiredFieldsMapping = useMemo(
    () =>
      errors
        ?.map((item) => {
          return allActiveRequirements[`${item?.toLowerCase()}`];
        })
        .filter(Boolean), // filter additional fields
    [allActiveRequirements, errors]
  );

  const additionalRequiredErrorFields =
    errors?.filter((field) =>
      additionalFields.includes(field?.toLowerCase())
    ) ?? [];

  const additionalRequiredFieldsMapping = additionalRequiredErrorFields.map(
    (field) => ({
      fullPathDisplayName:
        additionalRequirementFieldsLowercase[field].fieldDisplayName,
    })
  );

  return (
    <>
      <Space>
        <Typography.Text type='danger' style={{ marginBottom: 0 }} strong>
          Missing requirements
        </Typography.Text>
        <Tooltip title='Update values'>
          <Button
            type='text'
            size='small'
            icon={<ImportOutlined />}
            onClick={onModalRequiredActiveProduct}
          />
        </Tooltip>
        <Popover
          placement='right'
          content={() => (
            <ValidatingInfo
              requiredErrorFields={requiredFieldsMapping}
              additionalErrorFields={additionalRequiredFieldsMapping}
            />
          )}
        >
          <QuestionCircleTwoTone twoToneColor='#d93026' />
        </Popover>
      </Space>
      {visibleModalRequiredActiveProduct && (
        <RequiredActiveProductRoot>
          <ProductsActiveValidatingModal
            visible={visibleModalRequiredActiveProduct}
            onCancel={offModalRequiredActiveProduct}
            entityId={entityId}
            onRevalidateActiveProduct={onRevalidateActiveProduct}
            mode={mode}
          >
            <ProductsActiveValidating
              entityId={entityId}
              primaryErrors={primaryErrors}
              errors={errors}
              productData={productData}
              onRevalidateActiveProduct={onRevalidateActiveProduct}
              mode={mode}
              additionalRequirements={additionalRequirements}
            />
          </ProductsActiveValidatingModal>
        </RequiredActiveProductRoot>
      )}
    </>
  );
};

export const ValidatingInfo = ({
  requiredErrorFields,
  additionalErrorFields,
}) => {
  return (
    <FullWidthSpace
      className='scroller--y'
      style={{
        width: 300,
        maxHeight: 350,
        padding: '0px 8px',
        margin: '0px -8px',
      }}
    >
      <div>
        <Title level={5}>Properties Requirement</Title>
        <ErrorList errorFields={requiredErrorFields} />
      </div>
      <div>
        <Title level={5}>Additional Requirement</Title>
        <ErrorList errorFields={additionalErrorFields} />
      </div>
    </FullWidthSpace>
  );
};

const ErrorList = ({ errorFields }) => {
  return (
    <List
      dataSource={errorFields}
      renderItem={(item) => {
        return (
          <Row style={{ marginBottom: 4 }} gutter={16} wrap={false}>
            <Col flex={1}>
              <Typography.Text
                style={{
                  paddingLeft: 8,
                  lineHeight: 'normal',
                  display: 'inline-block',
                }}
              >
                {item.fullPathDisplayName}
              </Typography.Text>
            </Col>
            <Col flex={0}>
              <CloseOutlined style={{ color: '#cf1322', margin: '4px 0px' }} />
            </Col>
          </Row>
        );
      }}
    />
  );
};
